import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Copy, Check } from '@phosphor-icons/react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from './ui/dialog';
import SettingRow from './shell/SettingRow';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// stage: status | enrolling | recovery | disabling
const TwoFactorSettings = () => {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState('status');
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState(null); // {qr_data_uri, secret}
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState([]);
  const [copied, setCopied] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(setupData?.secret || '');
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    } catch (e) { /* clipboard denied */ }
  };

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/2fa/status`, { withCredentials: true });
      setEnabled(!!data.enabled);
    } catch (e) { /* ignore */ }
  }, []);

  const onOpenChange = (o) => {
    setOpen(o);
    if (o) { setStage('status'); setCode(''); setSetupData(null); setRecovery([]); loadStatus(); }
  };

  const startEnroll = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/2fa/setup`, {}, { withCredentials: true });
      setSetupData(data);
      setStage('enrolling');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start setup');
    } finally { setLoading(false); }
  };

  const confirmEnroll = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/2fa/enable`, { code: code.trim() }, { withCredentials: true }
      );
      setRecovery(data.recovery_codes || []);
      setEnabled(true);
      setCode('');
      setStage('recovery');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    } finally { setLoading(false); }
  };

  const disable = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/2fa/disable`, { code: code.trim() }, { withCredentials: true });
      setEnabled(false);
      setCode('');
      setStage('status');
      toast('Two-factor disabled');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    } finally { setLoading(false); }
  };

  const copyRecovery = async () => {
    try {
      await navigator.clipboard.writeText(recovery.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* clipboard denied */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <SettingRow icon={ShieldCheck} label="Two-factor authentication" data-testid="open-2fa" />
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Two-factor authentication</DialogTitle>
        </DialogHeader>

        {stage === 'status' && (
          <div className="space-y-4" data-testid="twofa-status">
            <p className="text-sm text-muted-foreground">
              {enabled
                ? 'Two-factor authentication is currently on. You enter a code from your authenticator app each time you sign in.'
                : 'Add a second step at sign-in using an authenticator app. Ente Auth and Aegis are free, open source, and tie you to no account. Required for staff accounts.'}
            </p>
            {enabled ? (
              <Button variant="outline" className="w-full text-destructive" onClick={() => setStage('disabling')} data-testid="twofa-disable-start">
                Turn off two-factor
              </Button>
            ) : (
              <Button className="w-full bg-primary hover:bg-primary/90" disabled={loading} onClick={startEnroll} data-testid="twofa-enable-start">
                {loading ? 'Preparing…' : 'Enable two-factor'}
              </Button>
            )}
          </div>
        )}

        {stage === 'enrolling' && setupData && (
          <form onSubmit={confirmEnroll} className="space-y-4" data-testid="twofa-enroll">
            <div className="rounded-lg border border-secondary/40 bg-muted/40 p-3 text-xs space-y-1">
              <p className="font-medium text-foreground">You need an authenticator app</p>
              <p className="text-muted-foreground">
                Google Authenticator, Authy, Microsoft Authenticator or 1Password.
                Open it and choose <span className="text-foreground">Add account → Scan QR code</span>.
              </p>
              <p className="text-muted-foreground">
                Scanning with your phone's camera will <span className="text-foreground">not</span> work —
                the 6-digit code only appears inside the authenticator app.
              </p>
            </div>
            <div className="flex justify-center">
              <img src={setupData.qr_data_uri} alt="2FA QR code" className="w-56 h-56 rounded-lg border border-border bg-white p-2" />
            </div>
            <div className="space-y-1">
              <p className="text-[11px] text-muted-foreground text-center">Can't scan? Enter this key manually:</p>
              <button
                type="button"
                onClick={copySecret}
                className="w-full font-mono text-xs break-all bg-muted/60 rounded p-2 border border-border hover:border-secondary/50"
                title="Click to copy"
              >
                {secretCopied ? 'Copied!' : setupData.secret}
              </button>
            </div>
            <div className="space-y-2">
              <Label htmlFor="enroll-code">Code</Label>
              <Input id="enroll-code" autoComplete="one-time-code" placeholder="Enter your code"
                value={code} onChange={(e) => setCode(e.target.value)} required autoFocus
                className="h-11 text-center tracking-[0.3em] placeholder:tracking-normal placeholder:text-sm" data-testid="twofa-enroll-code" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-primary hover:bg-primary/90" data-testid="twofa-enroll-confirm">
              {loading ? 'Verifying…' : 'Verify & turn on'}
            </Button>
          </form>
        )}

        {stage === 'recovery' && (
          <div className="space-y-4" data-testid="twofa-recovery">
            <div className="rounded-lg border border-secondary/40 bg-muted/40 p-3">
              <p className="text-sm font-medium text-foreground mb-1">Save your recovery codes</p>
              <p className="text-xs text-muted-foreground mb-3">
                Each code works once if you lose your authenticator. Store them somewhere safe — they won't be shown again.
              </p>
              <div className="grid grid-cols-2 gap-1.5 font-mono text-sm">
                {recovery.map((c) => (<div key={c} className="tabular-nums">{c}</div>))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={copyRecovery}>
                {copied ? <><Check className="w-4 h-4 mr-2" /> Copied</> : <><Copy className="w-4 h-4 mr-2" /> Copy</>}
              </Button>
              <Button className="flex-1 bg-primary hover:bg-primary/90" onClick={() => setStage('status')} data-testid="twofa-recovery-done">
                Done
              </Button>
            </div>
          </div>
        )}

        {stage === 'disabling' && (
          <form onSubmit={disable} className="space-y-4" data-testid="twofa-disabling">
            <p className="text-sm text-muted-foreground">
              Enter a current authenticator code (or a recovery code) to turn two-factor off.
            </p>
            <Input autoComplete="one-time-code" placeholder="Enter your code"
              value={code} onChange={(e) => setCode(e.target.value)} required autoFocus
              className="h-11 text-center tracking-[0.3em] placeholder:tracking-normal placeholder:text-sm" data-testid="twofa-disable-code" />
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => { setStage('status'); setCode(''); }}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="flex-1 text-destructive" variant="outline" data-testid="twofa-disable-confirm">
                {loading ? 'Turning off…' : 'Turn off'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TwoFactorSettings;
